import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { ArrowLeft, Eye, EyeOff, Save, X, Key } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import { updateUser } from "../services/crmServices/userService";
import { UpdatePasswordModal } from "../components/staff/UpdatePasswordModal";

export function EditProfile() {
    const navigate = useNavigate();
    const { user, updateProfile } = useAuth();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        userName: "",
        email: "",
        commpeak: "",
        voiso: "",
        squaretalk: "",
        sipAccount: "",
        commentColor: "#000000",
        password: "",
        canFastLogin: false,
        permissions: {
            leads: {
                view: false,
                create: false,
                update: false,
                delete: false,
                viewEmail: false,
                viewPhone: false,
                viewActionHistory: false
            },
            staff: {
                view: false,
                create: false,
                update: false,
                delete: false
            },
            desks: {
                view: false,
                create: false,
                update: false,
                delete: false
            },
            gridLeadsColumns: {
                source: false,
                affiliate: false,
                manager: false,
                registered: false,
                modified: false,
                desk: false,
                lastComment: false,
                lastCommDate: false,
                lastCommManager: false,
                age: false
            },
            editLeadFields: {
                firstName: false,
                lastName: false,
                userName: false,
                email: false,
                phone: false,
                secondPhone: false,
                status: false,
                country: false,
                city: false,
                address: false,
                source: false,
                extra: false
            }
        }
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handlePasswordUpdateSuccess = () => {
        setShowPasswordModal(false);
    };

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                userName: user.userName || "",
                email: user.email || "",
                commpeak: user.commpeak || "",
                voiso: user.voiso || "",
                squaretalk: user.squaretalk || "",
                sipAccount: user.sipAccount || "",
                commentColor: user.commentColor || "#000000",
                password: "",
                canFastLogin: user.canFastLogin || false,
                permissions: {
                    leads: {
                        view: user.permissions?.leads?.view || false,
                        create: user.permissions?.leads?.create || false,
                        update: user.permissions?.leads?.update || false,
                        delete: user.permissions?.leads?.delete || false,
                        viewEmail: user.permissions?.leads?.viewEmail || false,
                        viewPhone: user.permissions?.leads?.viewPhone || false,
                        viewActionHistory: user.permissions?.leads?.viewActionHistory || false
                    },
                    staff: {
                        view: user.permissions?.staff?.view || false,
                        create: user.permissions?.staff?.create || false,
                        update: user.permissions?.staff?.update || false,
                        delete: user.permissions?.staff?.delete || false
                    },
                    desks: {
                        view: user.permissions?.desks?.view || false,
                        create: user.permissions?.desks?.create || false,
                        update: user.permissions?.desks?.update || false,
                        delete: user.permissions?.desks?.delete || false
                    },
                    gridLeadsColumns: {
                        source: user.permissions?.gridLeadsColumns?.source || false,
                        affiliate: user.permissions?.gridLeadsColumns?.affiliate || false,
                        manager: user.permissions?.gridLeadsColumns?.manager || false,
                        registered: user.permissions?.gridLeadsColumns?.registered || false,
                        modified: user.permissions?.gridLeadsColumns?.modified || false,
                        desk: user.permissions?.gridLeadsColumns?.desk || false,
                        lastComment: user.permissions?.gridLeadsColumns?.lastComment || false,
                        lastCommDate: user.permissions?.gridLeadsColumns?.lastCommDate || false,
                        lastCommManager: user.permissions?.gridLeadsColumns?.lastCommManager || false,
                        age: user.permissions?.gridLeadsColumns?.age || false
                    },
                    editLeadFields: {
                        firstName: user.permissions?.editLeadFields?.firstName || false,
                        lastName: user.permissions?.editLeadFields?.lastName || false,
                        userName: user.permissions?.editLeadFields?.userName || false,
                        email: user.permissions?.editLeadFields?.email || false,
                        phone: user.permissions?.editLeadFields?.phone || false,
                        secondPhone: user.permissions?.editLeadFields?.secondPhone || false,
                        status: user.permissions?.editLeadFields?.status || false,
                        country: user.permissions?.editLeadFields?.country || false,
                        city: user.permissions?.editLeadFields?.city || false,
                        address: user.permissions?.editLeadFields?.address || false,
                        source: user.permissions?.editLeadFields?.source || false,
                        extra: user.permissions?.editLeadFields?.extra || false
                    }
                }
            });
        }
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.firstName || !formData.email || !formData.userName) {
            toast.error("Please fill in all required fields");
            return;
        }

        setLoading(true);

        try {
            const updateData = { ...formData, _id: user._id };

            // Only include password if it's provided
            if (!formData.password.trim()) {
                delete updateData.password;
            }

            const response = await updateUser(updateData);

            if (response?.data.RESULT === "ERROR") {
                toast.error(response.data.message);
            } else {
                toast.success("Profile updated successfully!");

                // Update the user data in localStorage
                const updatedUser = { ...user, ...formData };
                if (!formData.password.trim()) {
                    delete updatedUser.password;
                }
                updateProfile(updatedUser);
                localStorage.setItem("crm_user", JSON.stringify(updatedUser));

                navigate("/profile");
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            toast.error("Failed to update profile");
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionChange = (category, permission, checked) => {
        setFormData((prev) => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [category]: {
                    ...prev.permissions[category],
                    [permission]: checked
                }
            }
        }));
    };

    const handleCancel = () => {
        navigate("/profile");
    };

    if (!user) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">User not found</h3>
                    <p className="text-gray-500">Please log in to edit your profile.</p>
                </div>
            </div>
        );
    }

    // Safety check: ensure permissions object exists
    if (!formData.permissions) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Loading...</h3>
                    <p className="text-gray-500">Please wait while we load your profile data.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Button variant="outline" onClick={handleCancel} size="icon">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <h1 className="text-2xl font-bold text-gray-900">Edit Profile</h1>
                    </div>
                    <div className="flex space-x-3">
                        <Button
                            onClick={() => setShowPasswordModal(true)}
                            variant="outline"
                            className="border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                        >
                            <Key className="h-4 w-4 mr-2" />
                            Update Password
                        </Button>
                    </div>
                </div>

                {/* <div className="flex items-center space-x-4">
                    <Button variant="outline" onClick={handleCancel} size="icon">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h1 className="text-2xl font-bold text-gray-900">Edit Profile</h1>
                </div> */}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <Card>
                    <CardHeader>
                        <CardTitle>Basic Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="firstName">First Name *</Label>
                                <Input
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="lastName">Last Name</Label>
                                <Input
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="userName">User Name *</Label>
                                <Input
                                    id="userName"
                                    value={formData.userName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, userName: e.target.value }))}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="email">Email Address *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                                    required
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* System Integration */}
                <Card>
                    <CardHeader>
                        <CardTitle>System Integration</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="commpeak">Commpeak ID</Label>
                                <Input
                                    id="commpeak"
                                    value={formData.commpeak}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, commpeak: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="voiso">Voiso ID</Label>
                                <Input
                                    id="voiso"
                                    value={formData.voiso}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, voiso: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="squaretalk">SquareTalk ID</Label>
                                <Input
                                    id="squaretalk"
                                    value={formData.squaretalk}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, squaretalk: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="sipAccount">GlobalCall SIP Account</Label>
                                <Input
                                    id="sipAccount"
                                    value={formData.sipAccount}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, sipAccount: e.target.value }))}
                                    placeholder="e.g., agent.brand"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Your SIP account for GlobalCall Click2Call
                                </p>
                            </div>
                            <div>
                                <Label htmlFor="commentColor">Comment Color</Label>
                                <Input
                                    id="commentColor"
                                    type="color"
                                    value={formData.commentColor}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, commentColor: e.target.value }))}
                                />
                            </div>
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="canFastLogin"
                                    checked={formData.canFastLogin}
                                    onCheckedChange={(checked) =>
                                        setFormData((prev) => ({ ...prev, canFastLogin: checked }))
                                    }
                                />
                                <Label htmlFor="canFastLogin">Enable Fast Login</Label>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Security */}
                {/* <Card>
                    <CardHeader>
                        <CardTitle>Security</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="password">New Password (leave blank to keep current)</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                                        placeholder="Enter new password"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Leave empty if you don&apos;t want to change your password
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card> */}

                {/* Permissions - Read Only */}
                <Card>
                    <CardHeader>
                        <CardTitle>Permissions (Read-only)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div>
                            <h4 className="font-medium mb-3">Lead Permissions</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {formData.permissions?.leads && Object.keys(formData.permissions.leads).map((permission) => (
                                    <div key={permission} className="flex items-center space-x-2">
                                        <Switch
                                            id={`leads-${permission}`}
                                            checked={formData.permissions.leads[permission] || false}
                                            disabled={true}
                                        />
                                        <Label
                                            htmlFor={`leads-${permission}`}
                                            className="text-sm capitalize text-gray-500"
                                        >
                                            {permission.replace(/([A-Z])/g, " $1").trim()}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        <div>
                            <h4 className="font-medium mb-3">Staff Permissions</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {formData.permissions?.staff && Object.keys(formData.permissions.staff).map((permission) => (
                                    <div key={permission} className="flex items-center space-x-2">
                                        <Switch
                                            id={`staff-${permission}`}
                                            checked={formData.permissions.staff[permission] || false}
                                            disabled={true}
                                        />
                                        <Label
                                            htmlFor={`staff-${permission}`}
                                            className="text-sm capitalize text-gray-500"
                                        >
                                            {permission}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        <div>
                            <h4 className="font-medium mb-3">Desk Permissions</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {formData.permissions?.desks && Object.keys(formData.permissions.desks).map((permission) => (
                                    <div key={permission} className="flex items-center space-x-2">
                                        <Switch
                                            id={`desk-${permission}`}
                                            checked={formData.permissions.desks[permission] || false}
                                            disabled={true}
                                        />
                                        <Label
                                            htmlFor={`desk-${permission}`}
                                            className="text-sm capitalize text-gray-500"
                                        >
                                            {permission}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        {/* Grid Leads Columns Permissions */}
                        <div>
                            <h4 className="font-medium mb-3">Grid Leads Column Visibility</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {formData.permissions?.gridLeadsColumns && Object.keys(formData.permissions.gridLeadsColumns).map((column) => (
                                    <div key={column} className="flex items-center space-x-2">
                                        <Switch
                                            id={`columns-${column}`}
                                            checked={formData.permissions.gridLeadsColumns[column] || false}
                                            disabled={true}
                                        />
                                        <Label
                                            htmlFor={`columns-${column}`}
                                            className="text-sm capitalize text-gray-500"
                                        >
                                            {column.replace(/([A-Z])/g, " $1").trim()}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {formData.permissions?.editLeadFields && Object.keys(formData.permissions.editLeadFields).length > 0 && (
                            <>
                                <Separator />

                                {/* Lead Field Permissions */}
                                <div>
                                    <h4 className="font-medium mb-3">Lead Field Edit Permissions</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {Object.keys(formData.permissions.editLeadFields).map((field) => (
                                            <div key={field} className="flex items-center space-x-2">
                                                <Switch
                                                    id={`leadFields-${field}`}
                                                    checked={formData.permissions.editLeadFields[field] || false}
                                                    disabled={true}
                                                />
                                                <Label
                                                    htmlFor={`leadFields-${field}`}
                                                    className="text-sm capitalize text-gray-500"
                                                >
                                                    {field.replace(/([A-Z])/g, " $1").trim()}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800">
                                <strong>Note:</strong> Permissions can only be changed by administrators through the
                                Staff Management page.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Read-only Information */}
                <Card>
                    <CardHeader>
                        <CardTitle>Account Information (Read-only)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-gray-600">Position</Label>
                                <p className="text-sm text-gray-900 mt-1 p-2 bg-gray-50 rounded">
                                    {user.position?.name || user.position?.role || "N/A"}
                                </p>
                            </div>
                            <div>
                                <Label className="text-gray-600">View Leads Scope</Label>
                                <p className="text-sm text-gray-900 mt-1 p-2 bg-gray-50 rounded">
                                    {user.viewLeadsScope || "Assigned"}
                                </p>
                            </div>
                            {user.agentDesk && (
                                <div>
                                    <Label className="text-gray-600">Assigned Desk</Label>
                                    <p className="text-sm text-gray-900 mt-1 p-2 bg-gray-50 rounded">
                                        {user.agentDesk.name}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800">
                                <strong>Note:</strong> Position, account status, permissions, and desk assignments can
                                only be changed by administrators through the Staff Management page.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                    <Button type="submit" disabled={loading}>
                        <Save className="h-4 w-4 mr-2" />
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                    </Button>
                </div>
            </form>
            {/* Update Password Modal */}
            <UpdatePasswordModal
                open={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handlePasswordUpdateSuccess}
                staff={user}
            />
        </div>
    );
}
